import React, { type ReactElement, useCallback, useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import { useSelector } from 'react-redux'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material'

import { ChevronDownIcon, DefaultOrgIcon } from '~assets/icons'
import { Icon } from '~components/internal'
import { type RootState } from '~redux/rootReducer'
import { selectWorkspaceOrgs } from '~redux/slices/organizationSlice'
import { colors, typography } from '~tokens/internal'
import { type EmailIdentity } from '~types'

import Image from './Image'
import TextInfo from './TextInfo'

type OrganizationSelectorProps = {
  onSelect?: (option: EmailIdentity) => void
  placeholder?: string
  orgsOnly?: boolean
  preSelectOrg?: string
  orgHandle?: string
  requireConfirmation?: boolean
  confirmationMessage?: string
  confirmationInfo?: string
  searchable?: boolean
  useWorkspaceFiltering?: boolean
}

const OrganizationSelector = ({
  onSelect,
  placeholder = 'Select an identity',
  orgsOnly,
  preSelectOrg,
  orgHandle,
  requireConfirmation = false,
  confirmationMessage = 'Are you sure you want to change the organization?',
  confirmationInfo,
  searchable = false,
  useWorkspaceFiltering = false,
}: OrganizationSelectorProps): ReactElement => {
  const searchableOrgsThreshold = 10
  const forceSearchableThreshold = 20
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState<boolean>(false)
  const [pendingSelection, setPendingSelection] = useState<EmailIdentity | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const dropdownRef = useRef<HTMLDivElement>(null)
  const dropdownListRef = useRef<HTMLDivElement>(null)

  const selectedOrganizationIdLoad = preSelectOrg
    ? preSelectOrg
    : useSelector((state: RootState) => state?.organization.selectedOrganization?.id)
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<string | undefined>(
    selectedOrganizationIdLoad,
  )

  const workspaceOrgs = useSelector(selectWorkspaceOrgs)
  const allEmailIdentities = useSelector((state: RootState) => state?.organization.emailIdentities)
  const sourceIdentities = useWorkspaceFiltering ? workspaceOrgs : allEmailIdentities
  const emailIdentities =
    sourceIdentities?.filter((identity) => !orgsOnly || identity.organization_id) || []

  const filteredEmailIdentities = emailIdentities.filter((identity) =>
    identity.name?.toLowerCase().includes(searchTerm.toLowerCase()),
  )

  const orgRefreshTrigger = useSelector((state: RootState) => state?.organization.refreshTrigger)

  useEffect(() => {
    const selection = emailIdentities.find((identity) => identity.id === selectedOrganizationId)
    if (!!onSelect && selection) {
      onSelect(selection)
    }
  }, [orgRefreshTrigger])

  useEffect(() => {
    const selection = emailIdentities.find((identity) => identity.handle === orgHandle)
    if (selection?.id != selectedOrganizationId && !!onSelect && selection) {
      setSelectedOrganizationId(selection.id)
      onSelect(selection)
    } else if (emailIdentities.length > 0 && !!onSelect && !selectedOrganizationId) {
      const autoSelected = emailIdentities[0]
      setSelectedOrganizationId(autoSelected.id)
      onSelect(autoSelected)
    }
  }, [orgHandle])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent): void => {
      const target = e.target as Node
      const isClickInOriginal = dropdownRef.current && dropdownRef.current.contains(target)
      const isClickInDropdown = dropdownListRef.current && dropdownListRef.current.contains(target)

      if (!isClickInOriginal && !isClickInDropdown) {
        setIsOpen(false)
        if (searchTerm !== '') {
          setSearchTerm('')
        }
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return (): void => document.removeEventListener('mousedown', handleClickOutside)
  }, [searchTerm])

  const handleToggle = useCallback(() => {
    setIsOpen((prev) => !prev)
    if (!isOpen) {
      setSearchTerm('')
    }
  }, [isOpen])

  const handleOptionClick = useCallback(
    (option: EmailIdentity) => {
      setIsOpen(false)
      setSearchTerm('')
      if (requireConfirmation && option.id !== selectedOrganizationId) {
        setPendingSelection(option)
        setIsConfirmDialogOpen(true)
      } else {
        confirmSelection(option)
      }
    },
    [onSelect, requireConfirmation, selectedOrganizationId],
  )

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    event.stopPropagation()
    setSearchTerm(event.target.value)
  }

  const confirmSelection = (option: EmailIdentity): void => {
    if (!!onSelect) {
      setSelectedOrganizationId(option.id)
      onSelect(option)
    }
  }

  const handleConfirm = (): void => {
    if (pendingSelection) {
      confirmSelection(pendingSelection)
    }
    setIsConfirmDialogOpen(false)
    setPendingSelection(null)
  }

  const handleCancel = (): void => {
    setIsConfirmDialogOpen(false)
    setPendingSelection(null)
  }

  const showSearch = (): boolean => {
    if (emailIdentities.length >= forceSearchableThreshold) {
      return true
    }
    return searchable && emailIdentities.length >= searchableOrgsThreshold
  }

  const selectedOption = emailIdentities.find((option) => option.id === selectedOrganizationId)

  return (
    <>
      <div className={`org-selector ${isOpen ? 'active' : ''}`} ref={dropdownRef}>
        <div className='org-selector-header' onClick={handleToggle}>
          {!!selectedOption &&
            ((selectedOption.image ?? selectedOption.workspace_icon) ? (
              <Box sx={{ width: typography.fontSizePx.xl, height: typography.fontSizePx.xl }}>
                <Image
                  src={(selectedOption.image ?? selectedOption.workspace_icon) as string}
                  alt={selectedOption.name ? `${selectedOption.name} logo` : 'Organization logo'}
                  styles='profile-widget'
                  size={`${typography.fontSizePx.xl}px`}
                />
              </Box>
            ) : (
              <DefaultOrgIcon width={typography.fontSizePx.xl} height={typography.fontSizePx.xl} />
            ))}
          <p
            style={{
              marginLeft: '10px',
              maxWidth: 'calc(100% - 20px)',
              textOverflow: 'ellipsis',
              whiteSpace: 'normal',
            }}
          >
            {!!selectedOption ? selectedOption.name : placeholder}
          </p>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }} className='dropdown-arrow'>
            <ChevronDownIcon width={10} />
          </div>
        </div>
        {isOpen &&
          ReactDOM.createPortal(
            <div
              ref={dropdownListRef}
              className={`org-selector-list${useWorkspaceFiltering ? ' org-selector-list--v2' : ''}`}
              style={{
                position: 'fixed',
                zIndex: 1500,
                backgroundColor: useWorkspaceFiltering ? colors.sidebar.background : 'white',
                boxShadow: '0 8px 16px rgba(0,0,0,0.15)',
                borderRadius: '4px',
                maxHeight: '300px',
                overflowY: 'auto',
                width: dropdownRef.current
                  ? `${Math.max(dropdownRef.current.offsetWidth, 250)}px`
                  : '250px',
                top: dropdownRef.current
                  ? `${dropdownRef.current.getBoundingClientRect().bottom}px`
                  : '0',
                left: dropdownRef.current
                  ? `${dropdownRef.current.getBoundingClientRect().left}px`
                  : '0',
              }}
            >
              {showSearch() && (
                <Box
                  sx={{
                    p: 1,
                    position: 'sticky',
                    top: 0,
                    bgcolor: useWorkspaceFiltering ? '#121E48' : 'background.paper',
                    zIndex: 1,
                  }}
                >
                  <TextField
                    size='small'
                    fullWidth
                    placeholder='Search organizations...'
                    value={searchTerm}
                    onChange={handleSearchChange}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position='start'>
                            <Icon
                              name='search'
                              color={useWorkspaceFiltering ? 'rgba(255,255,255,0.5)' : undefined}
                            />
                          </InputAdornment>
                        ),
                      },
                    }}
                    sx={
                      useWorkspaceFiltering
                        ? {
                            '& .MuiOutlinedInput-root': {
                              color: 'white',
                              backgroundColor: '#121E48',
                              '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                              '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.4)' },
                              '&.Mui-focused fieldset': { borderColor: 'rgba(255,255,255,0.6)' },
                            },
                            '& .MuiOutlinedInput-input::placeholder': {
                              color: 'rgba(255,255,255,0.5)',
                              opacity: 1,
                            },
                          }
                        : undefined
                    }
                  />
                </Box>
              )}
              {filteredEmailIdentities?.map((option) => (
                <div
                  key={option.id}
                  className='org-selector-item'
                  onClick={() => handleOptionClick(option)}
                  style={{
                    color: useWorkspaceFiltering ? 'white' : 'inherit',
                    backgroundColor: useWorkspaceFiltering
                      ? colors.sidebar.hoverBackground
                      : 'inherit',
                    display: 'flex',
                    alignItems: 'center',
                    padding: '8px 12px',
                  }}
                >
                  <Box>
                    {(option.image ?? option.workspace_icon) ? (
                      <Image
                        src={(option.image ?? option.workspace_icon) as string}
                        alt={option.name ? `${option.name} logo` : 'Organization logo'}
                        styles='profile-widget'
                        size={`${typography.fontSizePx.xl}px`}
                      />
                    ) : (
                      <DefaultOrgIcon
                        width={typography.fontSizePx.xl}
                        height={typography.fontSizePx.xl}
                      />
                    )}
                  </Box>
                  <span style={{ marginLeft: '10px' }}>{option.name}</span>
                </div>
              ))}
            </div>,
            document.body,
          )}
      </div>
      <Dialog open={isConfirmDialogOpen} onClose={handleCancel}>
        <DialogTitle>Confirm Selection</DialogTitle>
        <DialogContent>
          <Typography>{confirmationMessage}</Typography>
          {!!confirmationInfo && <TextInfo maxWidth={600} message={confirmationInfo} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} color='secondary'>
            Cancel
          </Button>
          <Button onClick={handleConfirm} color='secondary' variant='contained'>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default OrganizationSelector
